import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { baseAPI } from '../../../config';

function lineDiff(oldText, newText) {
  const a = (oldText || '').split('\n');
  const b = (newText || '').split('\n');
  const m = a.length;
  const n = b.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (a[i] === b[j]) dp[i][j] = dp[i + 1][j + 1] + 1;
      else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const rows = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      rows.push({ type: 'equal', text: a[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      rows.push({ type: 'remove', text: a[i] });
      i++;
    } else {
      rows.push({ type: 'add', text: b[j] });
      j++;
    }
  }
  while (i < m) rows.push({ type: 'remove', text: a[i++] });
  while (j < n) rows.push({ type: 'add', text: b[j++] });
  return rows;
}

function formatDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  return d.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function renderValue(value) {
  if (value === null || value === undefined || value === '') return <span className='text-slate-400'>—</span>;
  if (Array.isArray(value)) return value.length === 0 ? <span className='text-slate-400'>—</span> : value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  return String(value);
}

const META_FIELDS = [
  { key: 'description', label: 'Descripción' },
  { key: 'ai_provider', label: 'Provider' },
  { key: 'model_id', label: 'Modelo' },
  { key: 'max_tokens', label: 'Max tokens' },
  { key: 'tools_enabled', label: 'Herramientas' },
];

const CONTEXT_FIELDS = [
  { key: 'interactions_limit', label: 'Límite de interacciones' },
  { key: 'include_annotations', label: 'Incluir anotaciones' },
  { key: 'include_documents', label: 'Incluir documentos' },
];

const AIAgentPromptHistory = ({ promptId, modalOpen, onClose, onReverted }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [reverting, setReverting] = useState(false);
  const [error, setError] = useState('');

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${baseAPI}/ai-system-prompts/${promptId}/history`, { headers });
      setHistory(response.data);
      const previous = response.data.find(v => v._id !== promptId);
      setSelectedId(previous?._id || null);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar el historial');
    } finally {
      setLoading(false);
    }
  }, [promptId, headers]);

  useEffect(() => {
    if (modalOpen && promptId) fetchHistory();
  }, [modalOpen, promptId, fetchHistory]);

  useEffect(() => {
    if (!modalOpen) return;
    const handler = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [modalOpen, onClose]);

  const current = useMemo(() => history.find(v => v._id === promptId), [history, promptId]);
  const selected = useMemo(() => history.find(v => v._id === selectedId), [history, selectedId]);

  const diffRows = useMemo(() => {
    if (!current || !selected) return [];
    return lineDiff(selected.prompt_text, current.prompt_text);
  }, [current, selected]);

  const handleRevert = async () => {
    if (!selected || !current) return;
    if (!window.confirm(`¿Revertir a la versión v${selected.version}? Se creará una nueva versión activa con este contenido.`)) return;

    setReverting(true);
    try {
      await axios.post(
        `${baseAPI}/ai-system-prompts/${current._id}/rollback`,
        { target_id: selected._id },
        { headers },
      );
      if (onReverted) onReverted();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al revertir');
    } finally {
      setReverting(false);
    }
  };

  if (!modalOpen) return null;

  const metaChanged = (field) => current && selected && JSON.stringify(current[field]) !== JSON.stringify(selected[field]);
  const contextChanged = (field) =>
    current &&
    selected &&
    JSON.stringify(current.context_config?.[field]) !== JSON.stringify(selected.context_config?.[field]);

  return (
    <>
      <div className='fixed inset-0 bg-gray-900 bg-opacity-30 z-50' onClick={onClose} />
      <div className='fixed inset-0 z-50 overflow-hidden flex items-center justify-center px-4'>
        <div
          className='bg-white rounded-lg shadow-lg w-full max-w-6xl max-h-[90vh] flex flex-col'
          onClick={e => e.stopPropagation()}
        >
          <div className='px-5 py-3 border-b border-gray-200 flex justify-between items-center'>
            <div className='font-semibold text-gray-800'>
              Historial del prompt{current ? ` · v${current.version} actual` : ''}
            </div>
            <button className='text-gray-400 hover:text-gray-500' onClick={onClose}>
              <svg className='w-4 h-4 fill-current'>
                <path d='M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z' />
              </svg>
            </button>
          </div>

          {loading ? (
            <div className='p-6 text-sm text-slate-500'>Cargando historial...</div>
          ) : error ? (
            <div className='p-6 text-sm text-red-600 bg-red-50'>{error}</div>
          ) : history.length <= 1 ? (
            <div className='p-6 text-sm text-slate-500'>No hay versiones anteriores.</div>
          ) : (
            <div className='flex-1 overflow-hidden flex'>
              {/* Version list */}
              <div className='w-60 border-r border-slate-200 overflow-y-auto'>
                {history.map(v => {
                  const isCurrent = v._id === promptId;
                  const isSelected = v._id === selectedId;
                  return (
                    <button
                      key={v._id}
                      type='button'
                      onClick={() => !isCurrent && setSelectedId(v._id)}
                      disabled={isCurrent}
                      className={`w-full text-left px-3 py-2 border-b border-slate-100 text-sm ${
                        isCurrent
                          ? 'bg-blue-50 cursor-default'
                          : isSelected
                          ? 'bg-slate-100'
                          : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className='flex items-center justify-between'>
                        <span className='font-medium text-slate-700'>v{v.version}</span>
                        {isCurrent && <span className='text-xs text-blue-600'>Actual</span>}
                      </div>
                      <div className='text-xs text-slate-500 mt-0.5'>
                        {formatDate(v.activated_at || v.createdAt)}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Diff panel */}
              <div className='flex-1 overflow-y-auto p-4'>
                {!selected ? (
                  <div className='text-sm text-slate-500'>Selecciona una versión para comparar.</div>
                ) : (
                  <>
                    <div className='flex items-center justify-between mb-3'>
                      <div className='text-sm text-slate-600'>
                        Comparando <span className='font-medium text-red-600'>v{selected.version}</span> →{' '}
                        <span className='font-medium text-green-600'>v{current.version} (actual)</span>
                      </div>
                      <button
                        type='button'
                        onClick={handleRevert}
                        disabled={reverting}
                        className='text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 rounded-lg px-3 py-1.5'
                      >
                        {reverting ? 'Revirtiendo...' : `Revertir a v${selected.version}`}
                      </button>
                    </div>

                    {/* Metadata diff */}
                    <div className='mb-4 rounded-lg border border-slate-200 overflow-hidden'>
                      <table className='w-full text-sm'>
                        <thead>
                          <tr className='bg-slate-50 text-xs uppercase text-slate-500'>
                            <th className='text-left px-3 py-2 w-48'>Campo</th>
                            <th className='text-left px-3 py-2'>v{selected.version}</th>
                            <th className='text-left px-3 py-2'>v{current.version} (actual)</th>
                          </tr>
                        </thead>
                        <tbody className='divide-y divide-slate-100'>
                          {META_FIELDS.map(f => {
                            const changed = metaChanged(f.key);
                            return (
                              <tr key={f.key} className={changed ? 'bg-amber-50' : ''}>
                                <td className='px-3 py-1.5 text-slate-600 font-medium'>{f.label}</td>
                                <td className='px-3 py-1.5 text-slate-700'>{renderValue(selected[f.key])}</td>
                                <td className='px-3 py-1.5 text-slate-700'>{renderValue(current[f.key])}</td>
                              </tr>
                            );
                          })}
                          {CONTEXT_FIELDS.map(f => {
                            const changed = contextChanged(f.key);
                            return (
                              <tr key={`ctx-${f.key}`} className={changed ? 'bg-amber-50' : ''}>
                                <td className='px-3 py-1.5 text-slate-600 font-medium'>{f.label}</td>
                                <td className='px-3 py-1.5 text-slate-700'>{renderValue(selected.context_config?.[f.key])}</td>
                                <td className='px-3 py-1.5 text-slate-700'>{renderValue(current.context_config?.[f.key])}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Prompt text diff */}
                    <div className='rounded-lg border border-slate-200 overflow-hidden'>
                      <div className='px-3 py-2 bg-slate-50 text-xs uppercase text-slate-500 font-semibold border-b border-slate-200'>
                        Texto del prompt
                      </div>
                      <pre className='text-xs font-mono leading-relaxed overflow-x-auto p-3 bg-white max-h-96'>
                        {diffRows.length === 0 ? (
                          <span className='text-slate-400'>(sin contenido)</span>
                        ) : (
                          diffRows.map((row, idx) => {
                            const bg =
                              row.type === 'add'
                                ? 'bg-green-50 text-green-800'
                                : row.type === 'remove'
                                ? 'bg-red-50 text-red-800'
                                : '';
                            const prefix = row.type === 'add' ? '+ ' : row.type === 'remove' ? '- ' : '  ';
                            return (
                              <div key={idx} className={`${bg} whitespace-pre-wrap`}>
                                <span className='select-none text-slate-400'>{prefix}</span>
                                {row.text || ' '}
                              </div>
                            );
                          })
                        )}
                      </pre>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <div className='px-5 py-3 border-t border-slate-200 flex justify-end'>
            <button
              type='button'
              onClick={onClose}
              className='text-sm font-medium text-slate-600 hover:text-slate-800 px-4 py-2'
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AIAgentPromptHistory;
